/**
 * /device — approve a Genie sign-in (RFC 8628 device flow).
 *
 * Used by both the desktop app and the browser studio (app.genie.art). Each
 * shows a short code and opens this page with `?user_code=XXXX-XXXX`. A
 * signed-in person confirms the code here; the other window, which is polling,
 * then receives its session. Not under `_auth` because that layout bounces
 * signed-in users to /workspace.
 *
 * `?from=studio` means the cloud gateway started the flow; `?from=desktop`
 * (or omitted with client genie-desktop) is the Mac/Windows app. Approving
 * hands out a full session, so a code that arrived in the link is never
 * one-click approved: the page shows who is asking and asks for a confirm
 * tick (RFC 8628 §5.4).
 */
import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'
import { zodValidator } from '@tanstack/zod-adapter'
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { authClient } from '@/lib/auth-client'
import { apiCall, isApiError } from '@/lib/api/api-helpers'
import { useAuth } from '@/hooks/useAuth'

const deviceSearchSchema = z.object({
  user_code: z.string().optional(),
  /** Who started the device flow — drives copy so cloud is not called "desktop". */
  from: z.enum(['studio', 'desktop']).optional(),
})

export const Route = createFileRoute('/device')({
  validateSearch: zodValidator(deviceSearchSchema),
  component: DeviceRoute,
})

function DeviceRoute() {
  const { user_code, from } = Route.useSearch()
  return <DevicePage userCodeFromLink={user_code} from={from} />
}

const STASH_KEY = 'genie.device.user_code'
const STASH_FROM = 'genie.device.from'

/** What /api/device/info says about the request behind a code. */
export interface DeviceRequestInfo {
  userCode: string
  clientId: string | null
  clientName: string | null
  scope: string | null
  status: 'pending' | 'approved' | 'denied' | 'expired'
  requestedAt: string
  expiresAt: string
}

/** Normalize to the plugin's XXXX-XXXX shape (letters upper-cased, one dash). */
function formatCode(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
  return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean
}

const isComplete = (code: string) => code.replace('-', '').length === 8

/** "just now" / "4 minutes ago" — enough to notice a code you did not just ask for. */
function describeAge(iso: string, now: number): string {
  const seconds = Math.round((now - new Date(iso).getTime()) / 1000)
  if (!Number.isFinite(seconds) || seconds < 45) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  return `${hours} hour${hours === 1 ? '' : 's'} ago`
}

type From = 'studio' | 'desktop' | undefined

/**
 * Who the copy should address. Once /api/device/info has answered, the client the server
 * recorded decides: `?from=` travels in the link, so anyone who sends the link controls it
 * and it must never relabel a request. Before that answer (signed out, or still loading)
 * `?from=` only picks the wording; with no hint at all the desktop app is assumed, since its
 * links carry no `from`.
 */
function resolveAudience(from: From, info: DeviceRequestInfo | null): 'studio' | 'desktop' | 'genie' {
  if (info) {
    if (info.clientId === 'genie-studio') return 'studio'
    if (info.clientId === 'genie-desktop') return 'desktop'
    return 'genie'
  }
  return from ?? 'desktop'
}

/** A client we issued is named by the page; anything else is flagged, whatever it calls itself. */
const isKnownClient = (info: DeviceRequestInfo) => info.clientId === 'genie-studio' || info.clientId === 'genie-desktop'

function copyFor(audience: 'studio' | 'desktop' | 'genie') {
  if (audience === 'studio') {
    return {
      signInTitle: 'Sign in to open Genie',
      signInBody: 'Genie in the browser is waiting for approval. Sign in here first, then confirm the code.',
      title: 'Open Genie',
      body: (email: string) => (
        <>Confirm the code shown in the other tab. You are signed in as <span className="text-foreground">{email}</span>.</>
      ),
      approvedTitle: 'Studio unlocked',
      approvedBody: (email: string) => `Signed in as ${email}. You can close this tab — Genie opens in the other one.`,
      confirm: (code: string) => (
        <>This code came from the link. Tick to confirm Genie in your other tab is showing <span className="font-mono text-foreground">{code}</span>.</>
      ),
      clientFallback: 'Genie in the browser',
      enterCode: 'Enter the 8-character code shown in Genie.',
      confirmError: 'Confirm the code matches the one Genie is showing.',
      footer: 'Only approve codes from a Genie window you opened yourself.',
    }
  }
  if (audience === 'desktop') {
    return {
      signInTitle: 'Sign in to connect your desktop',
      signInBody: 'The Genie app on your computer is waiting for approval. Sign in here first, then confirm the code it shows.',
      title: 'Connect your desktop',
      body: (email: string) => (
        <>Confirm the code shown in the Genie app. You are signed in as <span className="text-foreground">{email}</span>.</>
      ),
      approvedTitle: 'Desktop connected',
      approvedBody: (email: string) => `Signed in as ${email}. You can close this tab and go back to the app.`,
      confirm: (code: string) => (
        <>This code came from the link, not from you. Tick to confirm the Genie app on your computer is showing <span className="font-mono text-foreground">{code}</span>.</>
      ),
      clientFallback: 'Genie desktop app',
      enterCode: 'Enter the 8-character code shown in the app.',
      confirmError: 'Confirm the code matches the one your app is showing.',
      footer: 'Only approve codes from a device you are signing in yourself.',
    }
  }
  return {
    signInTitle: 'Sign in to continue',
    signInBody: 'Genie is waiting for approval. Sign in here first, then confirm the code.',
    title: 'Approve this sign-in',
    body: (email: string) => (
      <>Confirm the code shown in Genie. You are signed in as <span className="text-foreground">{email}</span>.</>
    ),
    approvedTitle: 'Signed in',
    approvedBody: (email: string) => `Signed in as ${email}. You can close this tab and return to Genie.`,
    confirm: (code: string) => (
      <>This code came from the link. Tick to confirm Genie is showing <span className="font-mono text-foreground">{code}</span>.</>
    ),
    clientFallback: 'Genie',
    enterCode: 'Enter the 8-character code shown in Genie.',
    confirmError: 'Confirm the code matches the one Genie is showing.',
    footer: 'Only approve codes from a Genie window you opened yourself.',
  }
}

interface DevicePageProps {
  userCodeFromLink?: string
  from?: From
}

export function DevicePage({ userCodeFromLink, from: fromProp }: DevicePageProps) {
  const { user, isLoading, isAuthenticated } = useAuth()
  const [code, setCode] = useState('')
  // A code we did not watch the person type: it came from the link (or the stash
  // we kept across the sign-in round trip), so it needs confirming before use.
  const [fromLink, setFromLink] = useState(false)
  const [from, setFrom] = useState<From>(fromProp)
  const [confirmed, setConfirmed] = useState(false)
  const [info, setInfo] = useState<DeviceRequestInfo | null>(null)
  const [infoState, setInfoState] = useState<'idle' | 'loading' | 'ready' | 'unknown' | 'unavailable'>('idle')
  const [state, setState] = useState<'idle' | 'busy' | 'approved' | 'denied' | 'error'>('idle')
  const [error, setError] = useState('')

  const audience = resolveAudience(from, info)
  const copy = copyFor(audience)

  // Keep the code (and from=) across the login round-trip.
  useEffect(() => {
    const fromUrl = userCodeFromLink ? formatCode(userCodeFromLink) : ''
    const stashed = (() => { try { return sessionStorage.getItem(STASH_KEY) ?? '' } catch { return '' } })()
    const stashedFrom = (() => {
      try {
        const v = sessionStorage.getItem(STASH_FROM)
        return v === 'studio' || v === 'desktop' ? v : undefined
      } catch { return undefined }
    })()
    const initial = fromUrl || stashed
    if (initial) {
      setCode(initial)
      setFromLink(true)
    }
    const nextFrom = fromProp || stashedFrom
    if (nextFrom) setFrom(nextFrom)
    try {
      if (fromUrl) sessionStorage.setItem(STASH_KEY, fromUrl)
      if (fromProp) sessionStorage.setItem(STASH_FROM, fromProp)
    } catch { /* private mode */ }
  }, [userCodeFromLink, fromProp])

  // Who is asking. Looked up as soon as there is a whole code and a session to ask with.
  useEffect(() => {
    if (!isAuthenticated || !isComplete(code)) { setInfo(null); setInfoState('idle'); return }
    let cancelled = false
    setInfoState('loading')
    apiCall(`/api/device/info?user_code=${encodeURIComponent(code)}`, true)
      .then((response) => {
        if (cancelled) return
        const data = (response as { data?: DeviceRequestInfo }).data ?? null
        setInfo(data)
        setInfoState(data ? 'ready' : 'unavailable')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setInfo(null)
        // A code we have never issued cannot be approved; anything else is our problem, not theirs.
        setInfoState(isApiError(err) && (err.status === 404 || err.status === 400) ? 'unknown' : 'unavailable')
      })
    return () => { cancelled = true }
  }, [code, isAuthenticated])

  const act = useCallback(async (kind: 'approve' | 'deny') => {
    if (!isComplete(code)) { setError(copy.enterCode); setState('error'); return }
    // Belt and braces: a linked code is never approved without the person confirming it.
    if (kind === 'approve' && fromLink && !confirmed) {
      setError(copy.confirmError)
      setState('error')
      return
    }
    setState('busy')
    setError('')
    const res = kind === 'approve'
      ? await authClient.device.approve({ userCode: code })
      : await authClient.device.deny({ userCode: code })
    if (res.error) {
      // The device plugin describes failures in error_description (expired, already used, wrong code).
      setError(res.error.error_description || 'That code was not accepted. Check it and try again.')
      setState('error')
      return
    }
    try {
      sessionStorage.removeItem(STASH_KEY)
      sessionStorage.removeItem(STASH_FROM)
    } catch { /* ignore */ }
    setState(kind === 'approve' ? 'approved' : 'denied')
  }, [code, fromLink, confirmed, copy.enterCode, copy.confirmError])

  const typeItMyself = () => {
    setCode('')
    setFromLink(false)
    setConfirmed(false)
    setInfo(null)
    setInfoState('idle')
    try { sessionStorage.removeItem(STASH_KEY) } catch { /* ignore */ }
  }

  const deadCode = infoState === 'unknown' || (infoState === 'ready' && info?.status !== 'pending')
  // Nothing is approved while the page is still finding out who is asking.
  const canApprove = state !== 'busy' && isComplete(code) && infoState !== 'loading' && !deadCode && (!fromLink || confirmed)

  const redirectSearch = (() => {
    const q = new URLSearchParams()
    if (code) q.set('user_code', code)
    if (from) q.set('from', from)
    const s = q.toString()
    return s ? `/device?${s}` : '/device'
  })()

  // Our own clients get our wording; any other client is flagged as unrecognised.
  const whoIsAsking = (() => {
    if (!info || isKnownClient(info)) return copy.clientFallback
    return info.clientName ?? 'An app we do not recognise'
  })()

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <GenieLogo className="mb-4 h-10 w-10" />
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Checking your session…</p>
          ) : !isAuthenticated ? (
            <>
              <h1 className="text-lg font-semibold tracking-tight">{copy.signInTitle}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{copy.signInBody}</p>
            </>
          ) : state === 'approved' ? (
            <>
              <h1 className="text-lg font-semibold tracking-tight">{copy.approvedTitle}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{copy.approvedBody(user?.email ?? '')}</p>
            </>
          ) : state === 'denied' ? (
            <>
              <h1 className="text-lg font-semibold tracking-tight">Request denied</h1>
              <p className="mt-1 text-sm text-muted-foreground">Nothing was connected. You can close this tab.</p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold tracking-tight">{copy.title}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{copy.body(user?.email ?? '')}</p>
            </>
          )}
        </div>

        {!isLoading && !isAuthenticated && (
          <div className="space-y-3">
            <Button asChild size="lg" className="h-10 w-full rounded-lg text-sm font-medium">
              <Link to="/login" search={{ redirect: redirectSearch }}>Sign in</Link>
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              New to Genie? <Link to="/signup" search={{ redirect: redirectSearch }} className="underline-offset-4 hover:underline">Create an account</Link>
            </p>
          </div>
        )}

        {!isLoading && isAuthenticated && state !== 'approved' && state !== 'denied' && (
          <div className="space-y-3">
            {fromLink ? (
              <div className="rounded-xl border border-input py-5 text-center font-mono text-3xl tracking-[0.3em]" aria-label="Sign-in code">{code}</div>
            ) : (
              <Input
                value={code}
                onChange={(e) => { setCode(formatCode(e.target.value)); if (state === 'error') setState('idle') }}
                placeholder="XXXX-XXXX"
                autoFocus
                spellCheck={false}
                autoComplete="one-time-code"
                aria-label="Sign-in code"
                className="h-20 rounded-xl text-center font-mono text-3xl tracking-[0.3em] placeholder:tracking-[0.3em]"
              />
            )}

            {isComplete(code) && (
              <div className="rounded-lg border border-input p-3 text-sm">
                {infoState === 'loading' && <p className="text-muted-foreground">Checking who is asking…</p>}
                {infoState === 'unknown' && <p className="text-destructive">No sign-in request is waiting for this code. Check the code in Genie, or start the sign-in there again.</p>}
                {infoState === 'unavailable' && <p className="text-destructive">We could not check who is asking. Only continue if you started this sign-in yourself.</p>}
                {infoState === 'ready' && info && (
                  <>
                    <p className="text-foreground">
                      {whoIsAsking} asked to sign in as <span className="font-medium">{user?.email}</span>.
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      Requested {describeAge(info.requestedAt, Date.now())}
                      {!isKnownClient(info) ? ` by “${info.clientId ?? 'unknown client'}”` : ''}
                      {info.scope ? ` · asks for ${info.scope}` : ''}
                    </p>
                    {info.status === 'expired' && <p className="mt-1 text-destructive">This code has expired. Start the sign-in again in Genie.</p>}
                    {info.status === 'approved' && <p className="mt-1 text-destructive">This code was already used.</p>}
                    {info.status === 'denied' && <p className="mt-1 text-destructive">This code was already denied.</p>}
                  </>
                )}
              </div>
            )}

            {fromLink && !deadCode && (
              <label htmlFor="device-code-confirm" className="flex items-start gap-2 text-left text-xs text-muted-foreground">
                <Checkbox id="device-code-confirm" checked={confirmed} onCheckedChange={(checked) => setConfirmed(checked === true)} aria-label="Confirm the code matches" className="mt-0.5" />
                <span>{copy.confirm(code)}</span>
              </label>
            )}

            {state === 'error' && <p className="text-center text-sm text-destructive">{error}</p>}

            <div className="flex gap-2">
              <Button variant="outline" size="lg" className="h-10 flex-1 rounded-lg text-sm" disabled={state === 'busy'} onClick={() => void act('deny')}>Deny</Button>
              <Button size="lg" className="h-10 flex-1 rounded-lg text-sm font-medium" disabled={!canApprove} onClick={() => void act('approve')}>
                {state === 'busy' ? 'Connecting…' : 'Approve'}
              </Button>
            </div>

            {fromLink && (
              <button type="button" onClick={typeItMyself} className="w-full text-center text-xs text-muted-foreground underline-offset-4 hover:underline">
                Type the code myself instead
              </button>
            )}
            <p className="text-center text-xs text-muted-foreground">{copy.footer}</p>
          </div>
        )}
      </div>
    </div>
  )
}
